import { useMemo, useState } from 'react';
import { Apartment } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { companiesAction, fundsAction, globalAction } from 'common/actions';
import { firmIntelligenceUrl } from 'common/config/urls';
import { CREATE_FIRM_ENABLER } from 'common/constants/features';
import { useStore } from 'common/store';
import type { Firm } from 'common/types/firm';
import type { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import { PrimaryNavigationChip } from 'components/ui';
import { getCssPx } from 'components/ui/utilities/zoom';
import { getInitials } from 'utilities/getInitials';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from './PrimaryNavigation.testIds';
import FirmSwitcherMenu from './PrimaryNavigationOptionsMenu/FirmSwitcherMenu';

interface FirmChipProps {
  zoomLevel: number;
}

const FirmChip = ({ zoomLevel }: FirmChipProps) => {
  const { firmSlugParam } = useParams<{ firmSlugParam: string }>();
  const navigate = useNavigate();
  const [{ firmInfo, firmList, user }, dispatch] = useStore() as unknown as UseStoreValues;
  const [, checkFeature] = useCheckFeatures();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const firms = useMemo(() => (firmList as Firm[] | null | undefined) ?? [], [firmList]);
  const firmName = firmInfo?.name ?? 'Scalar';
  const canAddFirm = Boolean(user?.is_superuser || checkFeature(CREATE_FIRM_ENABLER));
  const canOpenFirmMenu = firms.length > 1 || canAddFirm;

  const firmIcon = firmInfo?.icon_url ? (
    <Avatar
      alt={`${firmName} firm icon`}
      src={firmInfo.icon_url}
      variant="rounded"
      sx={{
        borderRadius: getCssPx(4),
        fontSize: getCssPx(7),
        height: getCssPx(16),
        width: getCssPx(16),
      }}>
      {getInitials({ name: firmName })}
    </Avatar>
  ) : (
    <Apartment />
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (canOpenFirmMenu) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFirmSelect = (firm: Firm) => {
    dispatch(companiesAction.setCompanyList(null));
    dispatch(fundsAction.setFundList(null));
    navigate(firmIntelligenceUrl(firm.slug));
    handleClose();
  };

  const handleAddFirm = () => {
    handleClose();
    dispatch(globalAction.showFirmStepper(true));
  };

  return (
    <>
      <PrimaryNavigationChip
        aria-haspopup={canOpenFirmMenu ? 'listbox' : undefined}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.firmChip}
        icon={firmIcon}
        onClick={canOpenFirmMenu ? handleOpen : undefined}
        showDropdownIcon={canOpenFirmMenu}
        value={firmName}
        valueTone="strong"
        zoomLevel={zoomLevel}
      />

      {canOpenFirmMenu && (
        <FirmSwitcherMenu
          activeFirmSlug={firmSlugParam}
          anchorEl={anchorEl}
          canAddFirm={canAddFirm}
          firms={firms}
          onAddFirm={handleAddFirm}
          onClose={handleClose}
          onFirmSelect={handleFirmSelect}
          zoomLevel={zoomLevel}
        />
      )}
    </>
  );
};

export default FirmChip;
